import type { Character } from '../domain/character.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { inEncounter } from '../domain/runtime.ts';
import { encounterActionProblem } from '../rules/encounter.ts';
import type { EncounterActionTiming } from '../rules/encounter.ts';
import type { Skill } from '../content/types.ts';
import type { HotlistCatalogue } from '../rules/hotlist.ts';
import { isHealingInterruptSpell, isManualCustomSpell, isPassiveSpell, isSpell, spellCastActionCost } from '../rules/spellCast.ts';
import { canRollSkill } from '../rules/skillAvailability.ts';
import { spendEncounterAction } from './encounterService.ts';
import { healCharacterSlots, spendCharacterMana } from './healthService.ts';

/**
 * Casting a support Spell - one that heals or otherwise acts without an Attack
 * Check. RULE DCC-SPELL-003.
 *
 * **EVERYTHING MECHANICAL COMES FROM CONTENT.** The Mana cost is the Skill's
 * `manaCost`, the absence of a Check is its `passive` trait, the Rank ceiling
 * is its authored Limitation. Nothing here knows that Heal costs 2 Mana or
 * restores 2 slots, because the previous implementation did know, hard-coded
 * both, and drifted from the book it was copied out of.
 *
 * That predecessor lived in `PlayScreen.handleCastHeal` and told the player
 * outright: "Spend the Action yourself - this button does not yet cost one."
 * It is retired. There is exactly one mechanical Heal now.
 *
 * PURE-THEN-PERSIST, like every other priced Action. Mana and the Action move
 * together or not at all: a refusal writes nothing, so nothing can be spent on
 * a cast that did not happen.
 *
 * TWO OPERATIONS, ONE IMPLEMENTATION. Rules p.19 Table 3 lists Heal as an
 * Interrupt that "can also be used as a non-Interrupt Action inside or outside
 * of combat", so the same Spell is two legal things at two different moments.
 * The mechanics are identical; only WHEN it may happen differs, so the caller
 * states which operation it means and everything else is shared.
 */

/**
 * Which operation the caller is performing. NO DEFAULT, deliberately.
 *
 * The alternative - reading `skill.traits.includes('interrupt')` and switching
 * on the current phase - is exactly the ambiguity B1 removed. It would mean the
 * ordinary Cast control quietly became an Interrupt during the Reaction Phase,
 * and the rule that applied would be decided by which button the player
 * happened to press rather than by what they asked for.
 *
 * This is NOT `EncounterActionTiming`. That type is about WHEN an Action may
 * happen and knows nothing about Spells; exposing it here would make every
 * future support Spell with an `interrupt` trait - Protective Shell, Shield -
 * accidentally executable as a generic Interrupt with mechanics nobody wrote.
 * This union names OPERATIONS, and each one has to be earned.
 */
export type SupportSpellExecution = 'normal' | 'healing-interrupt';

export type SpellCastOutcome =
  | {
      ok: true;
      character: Character;
      /** UNCHANGED BY REFERENCE outside a fight, so no journal is opened. */
      runtime: RuntimeState;
      lines: string[];
      /** How many Actions this cost. Zero outside an encounter. */
      actionsSpent: number;
    }
  | { ok: false; reason: string };

export interface SpellCastDeps {
  catalogue: HotlistCatalogue;
}

export function castSupportSpell(
  character: Character,
  runtime: RuntimeState,
  skill: Skill,
  deps: SpellCastDeps,
  execution: SupportSpellExecution,
): SpellCastOutcome {
  // 1. IDENTITY. A RuntimeState is only evidence about the crawler it names.
  if (runtime.characterId !== character.id) {
    return {
      ok: false,
      reason:
        `Internal error: this cast is for ${character.id} but the runtime supplied belongs to ` +
        `${runtime.characterId}. Nothing has been changed.`,
    };
  }

  if (!isSpell(skill)) return { ok: false, reason: `${skill.name} is not a Spell.` };

  // 2. KNOWN, and known well enough. RULE DCC-CHK-004 - a Spell cannot be
  // attempted without Ranks (Rules p.12), and that is true of a Spell that
  // makes no Check as much as one that does: the Rank is what the crawler has.
  const known = character.skills.find((entry) => entry.skillId === skill.id);
  if (known === undefined || known.rank < 1) {
    return {
      ok: false,
      reason: `${skill.name} is not a Spell this crawler knows at Rank 1 or better.`,
    };
  }

  // RULE DCC-SPELL-004 - the AUTHORED ceiling, e.g. Heal's "Limitations: Rank 1
  // maximum" (C&C p.38). A crawler holding it above that has a sheet the rules
  // do not allow, whether by an old save, an import or an edit made before the
  // ceiling was recorded. Refusing here is the last line; `applyChanges`
  // refuses to create the state in the first place when a catalogue is present.
  if (skill.maxRank !== undefined && known.rank > skill.maxRank) {
    return {
      ok: false,
      reason:
        `${skill.name} is limited to Rank ${skill.maxRank} and this crawler holds it at ` +
        `Rank ${known.rank}. Correct the Rank before casting.`,
    };
  }

  // 3. THIS PATHWAY IS FOR SPELLS THAT MAKE NO CHECK. An Attack Spell rolls,
  // needs a target and a mode, and belongs to `resolveAttackAction` - there is
  // one Attack resolver and this is not a second one.
  if (!isPassiveSpell(skill)) {
    return {
      ok: false,
      reason:
        `${skill.name} requires an Attack Skill Check. Cast it from the Attack panel, which ` +
        'takes the target and the roll mode.',
    };
  }
  // Belt and braces, through the shared rule rather than a second opinion.
  if (canRollSkill(skill, known.rank).rollable) {
    return { ok: false, reason: `${skill.name} makes a Skill Check and cannot be cast here.` };
  }

  // FROM THE SKILL'S OWN CONTENT. There is no lookup table keyed by Spell ID:
  // one existed, held Heal's two slots as a TypeScript constant, and was simply
  // the old hard-coded number in a new house.
  const effect = skill.supportEffect;
  if (effect === undefined && !isManualCustomSpell(skill)) {
    return { ok: false, reason: `This application cannot yet carry out ${skill.name}.` };
  }
  if (effect && effect.target !== 'self') {
    return { ok: false, reason: `${skill.name} targets a companion or other creature. Use its target-specific control.` };
  }

  // 3b. IS THIS SPELL ELIGIBLE FOR THE OPERATION THE CALLER ASKED FOR?
  //
  // RULE DCC-ENC-005. Everything above already established that this is a known
  // Passive Spell with an authored effect. A Healing Interrupt additionally
  // needs the Spell to be an Interrupt AND to actually heal, and BOTH from
  // authored data - never from the name, the ID, the description or the Mana
  // cost. No branch anywhere in this pathway compares the Skill ID, and
  // the supplied Heal qualifies because of what its content says, not what it
  // is called.
  //
  // The second half matters as much as the first. Protective Shell and Shield
  // carry the `interrupt` trait too (C&C pp.39-40) and their mechanics are not
  // written; treating the trait alone as permission would make them silently
  // executable as heals.
  if (execution === 'healing-interrupt' && !isHealingInterruptSpell(skill)) {
    return {
      ok: false,
      reason:
        `${skill.name} cannot be cast as a Healing Interrupt: it must be an Interrupt Spell ` +
        'with an authored healing effect this application can carry out.',
    };
  }

  // 4. THE ACTION COST, from the crawler's CURRENT Hotlist rather than from
  // whichever control was pressed (RULE DCC-SPELL-002). THE SAME RULE FOR BOTH
  // OPERATIONS - an Interrupt gets no special discount. Rules p.33 is explicit
  // that Reaction Phase spending "counts against the two Actions that each
  // crawler gets every round".
  const fighting = inEncounter(runtime);
  const actionCost = spellCastActionCost(character, skill.id, deps.catalogue);

  // 5. TIMING AND AFFORDABILITY, THROUGH THE SHARED AUTHORITY, BEFORE ANYTHING
  // MOVES. RULE DCC-ENC-003.
  //
  // This used to count `actionsRemaining` by hand and left the PHASE question
  // to the final spend, which meant the phase was discovered after the Mana had
  // been paid and the Heal applied. The function is pure, so nothing reached
  // disk - but "we built the whole Action and then threw it away" is not a
  // preflight, and the second Action-count rule was a copy waiting to drift.
  //
  // An ordinary cast, so Crawler Action Phase only. Heal carries the
  // `interrupt` trait and it is NOT read here: a Healing Interrupt is a
  // separate operation the caller asks for deliberately (Foundation B2).
  // Pressing the ordinary Cast control during the Reaction Phase must refuse,
  // or UI origin would be deciding which rule applied.
  // The operation the caller named decides the WINDOW. This is the only place
  // `'interrupt'` timing enters the Spell pathway, and it is reached only after
  // the eligibility gate above.
  const timing: EncounterActionTiming = execution === 'healing-interrupt' ? 'interrupt' : 'normal';
  const timingProblem = encounterActionProblem(runtime, character.id, actionCost, timing);
  if (timingProblem !== null) {
    return {
      ok: false,
      reason:
        actionCost === 2
          ? `${skill.name} costs 2 Actions because it is not prepared in the Hotlist. ` +
            timingProblem
          : `${skill.name} costs ${actionCost} Action. ${timingProblem}`,
    };
  }

  const cost = skill.manaCost ?? 0;
  const paid = spendCharacterMana(character, cost);
  if (!paid.ok) return { ok: false, reason: `${skill.name} costs ${cost} Mana. ${paid.reason}` };

  // 6. THE EFFECT, from content.
  let next = paid.character;
  const lines: string[] = [];
  if (effect?.kind === 'heal-slots') {
    const before = next.healthBarSlotsRemaining;
    next = healCharacterSlots(next, effect.slots);
    lines.push(
      `${skill.name} cast for ${cost} Mana: ` +
        `${next.healthBarSlotsRemaining - before} Health Bar slot` +
        `${next.healthBarSlotsRemaining - before === 1 ? '' : 's'} restored.`,
    );
  }

  if (isManualCustomSpell(skill)) lines.push(`${skill.name} cast for ${cost} Mana. The GM resolves the described effect manually: ${skill.description ?? ''}`);

  // 7. THE ACTION, through the shared pathway so `after-next-action` expiry
  // behaves exactly as it does for every other Action.
  let nextRuntime = runtime;
  let actionsSpent = 0;
  if (fighting) {
    // The preflight above already settled legality. This still runs because it
    // is what actually DECREMENTS the pool and fires `after-next-action` - and
    // because a defensive recheck at the point of spending costs nothing.
    const spent = spendEncounterAction(nextRuntime, character.id, actionCost, timing);
    if (!spent.ok) return { ok: false, reason: spent.reason };
    nextRuntime = spent.runtime;
    actionsSpent = actionCost;
    lines.push(
      `${actionCost} Action${actionCost === 1 ? '' : 's'} spent` +
        `${actionCost === 2 ? ' - not prepared in the Hotlist' : ''}.`,
    );
    for (const id of spent.expiredEffectIds) lines.push(`${id} ended with that Action`);
  }

  return { ok: true, character: next, runtime: nextRuntime, lines, actionsSpent };
}
